import { AxiosResponse } from 'axios'

import { PaginatedResponse } from 'api/Cerebro/types'
import { orgScopedApiClient } from 'api/orgScopedClient'
import { DATA_SOURCES } from 'configuration/widgets'
import { TABLE } from 'const/widgets'
import { DataSource, DataSourceMetric } from 'features/MetricsGlossary/types'
import { MetricCapabilities } from 'helpers/ui/dashboardPage'
import { Widget } from 'types'
import queryClientRegistry from 'utilities/queryClientRegistry'

export function widgetNeedsMetricCapabilities(widget: Widget): boolean {
    return widget.type === TABLE && !!widget.show_totals
}

export function buildMetricCapabilities(
    metrics: DataSourceMetric[]
): MetricCapabilities {
    const totalSupporting = new Set<string>()
    for (const metric of metrics) {
        if (metric.metadata_supports_total) {
            for (const name of metric.metadata_all_names) {
                totalSupporting.add(name)
            }
        }
    }
    return {
        supportsTotal: (id) => totalSupporting.has(id),
    }
}

function readCachedDataSources(): DataSource[] {
    const queryClient = queryClientRegistry.getClient()
    if (!queryClient) {
        return []
    }
    return queryClient
        .getQueriesData<AxiosResponse<PaginatedResponse<DataSource>> | null>({
            queryKey: ['dataSourceList'],
        })
        .flatMap(([, value]) => value?.data.results ?? [])
}

function readCachedMetricsForSlug(
    slug: string
): DataSourceMetric[] | undefined {
    const queryClient = queryClientRegistry.getClient()
    if (!queryClient) {
        return undefined
    }

    const matches = queryClient
        .getQueriesData<AxiosResponse<
            PaginatedResponse<DataSourceMetric>
        > | null>({
            queryKey: ['dataSourceMetricsList'],
        })
        .filter(([key]) => {
            if (!Array.isArray(key)) {
                return false
            }
            const params = key[1] as { dataSourceSlug?: string } | undefined
            return params?.dataSourceSlug === slug
        })
        .map(([, value]) => value?.data.results)

    const withResults = matches.find((results) => results != null)
    return withResults ?? undefined
}

async function fetchDataSources(): Promise<DataSource[]> {
    const response = await orgScopedApiClient.get<
        PaginatedResponse<DataSource>
    >('/api/data_sources/', {
        params: {
            include_pre_release: true,
            include_end_of_life: true,
        },
    })
    return response.data.results ?? []
}

async function fetchMetricsForSlug(slug: string): Promise<DataSourceMetric[]> {
    const response = await orgScopedApiClient.get<
        PaginatedResponse<DataSourceMetric>
    >(`/api/data_source/${slug}/metrics/`, {
        params: {
            data_source: slug,
            include_not_visible: true,
            include_pre_release: true,
            include_end_of_life: true,
            limit: 1000,
            offset: 0,
        },
    })
    return response.data.results ?? []
}

function resolveSlug(
    dataSources: DataSource[],
    metadataKey: string | undefined
): string | undefined {
    if (!metadataKey) {
        return undefined
    }
    return dataSources.find((d) => d.metadata_key === metadataKey)?.slug
}

/**
 * Resolve glossary-based total-support capabilities for a widget.
 * Returns undefined when capabilities are not needed or the data source is
 * not in the glossary (legacy: getWidgetMetrics includes all __total variants).
 * When the data source is in the glossary, fetches on cache miss rather than
 * falling back to "all metrics support totals".
 */
export async function resolveMetricCapabilities(
    widget: Widget
): Promise<MetricCapabilities | undefined> {
    if (!widgetNeedsMetricCapabilities(widget)) {
        return undefined
    }

    const dataSourceConfig = DATA_SOURCES[widget.data_source]
    const metadataKey = dataSourceConfig?.getMetadataKey(widget)

    let dataSources = readCachedDataSources()
    let slug = resolveSlug(dataSources, metadataKey)

    if (!slug) {
        dataSources = await fetchDataSources()
        slug = resolveSlug(dataSources, metadataKey)
    }

    if (!slug) {
        return undefined
    }

    const cachedMetrics = readCachedMetricsForSlug(slug)
    const metrics = cachedMetrics ?? (await fetchMetricsForSlug(slug))

    return buildMetricCapabilities(metrics)
}
